import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import { SECCIONES_DE_ANTECEDENTES, idDeLaSeccion, type ClaveDeSeccion } from "./antecedentesGenerales";

interface TarjetaDeSeccionProps {
  clave: ClaveDeSeccion;
  titulo: string;
  bajada?: string;
  /** Lo que va a la derecha del encabezado: chips de estado, botones. */
  acciones?: ReactNode;
  /** Un borde que avisa que la sección tiene un dato obligatorio sin resolver. */
  conAviso?: boolean;
  children: ReactNode;
}

/**
 * Una tarjeta numerada de "Antecedentes generales": el cuadradito con el 01…09,
 * el título, su bajada y el cuerpo.
 *
 * El número sale de `SECCIONES_DE_ANTECEDENTES`, el mismo arreglo que dibuja el
 * índice de la derecha: con la numeración escrita en cada tarjeta, reordenar
 * una dejaría el índice y los números diciendo cosas distintas.
 */
export function TarjetaDeSeccion({
  clave,
  titulo,
  bajada,
  acciones,
  conAviso = false,
  children,
}: TarjetaDeSeccionProps) {
  const numero = SECCIONES_DE_ANTECEDENTES.find((s) => s.clave === clave)?.n ?? "";

  return (
    <section
      id={idDeLaSeccion(clave)}
      aria-labelledby={`${idDeLaSeccion(clave)}-titulo`}
      className={cn(
        "bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5",
        conAviso && "border-danger-dot/40",
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="bg-superficie-fija text-superficie-fija-foreground flex size-7 flex-none items-center justify-center rounded-sm font-mono text-xs font-bold">
          {numero}
        </span>
        <div className="min-w-0">
          <h2 id={`${idDeLaSeccion(clave)}-titulo`} className="text-h3 text-ink">
            {titulo}
          </h2>
          {bajada ? <p className="text-caption text-ink-hint mt-px">{bajada}</p> : null}
        </div>
        {acciones ? <div className="ml-auto flex flex-none items-center gap-2">{acciones}</div> : null}
      </div>

      <div className="mt-4">{children}</div>
    </section>
  );
}
